// =============================================================================
// reports.ts — Contratos Zod compartilhados para o módulo de relatórios (F23-S03).
//
// Importado tanto pelo backend (apps/api) quanto pelo frontend (apps/web) para
// garantir contratos idênticos e evitar drift front×API (ver memória do projeto).
//
// LGPD (doc 17 §3.3 finalidade 8):
//   - Nenhum campo de PII nos responses (nome, CPF, telefone, email, endereço).
//   - Apenas contagens, somatórios, médias e IDs opacos (UUIDs de cidades/agentes/stages).
//
// Multi-tenant: organization_id é implícito no contexto do actor — nunca exposto
// como filtro de query (o backend injeta do JWT).
// =============================================================================
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
/**
 * Ranges de relatório — espelha `REPORT_RANGES` de apps/api/src/shared/dates.ts.
 *
 * `yesterday`, `thisWeek` e `ytd` entraram na correção do diagnóstico de
 * dashboard/relatórios: são os cortes que a operação usa no fechamento diário e
 * que só existiam num dos dois painéis (D5/U5).
 */
export const ReportRangeEnum = z.enum([
    'today',
    'yesterday',
    'thisWeek',
    'last7d',
    'last30d',
    'last90d',
    'thisMonth',
    'lastMonth',
    'ytd',
    'custom',
]);
export const ReportScopeEnum = z.enum(['global', 'city', 'self']);
// ---------------------------------------------------------------------------
// Filtros comuns
// ---------------------------------------------------------------------------
// O parser de querystring do Fastify entrega `string` quando um param aparece
// uma única vez (`?cityIds=a`) e `string[]` quando repete (`?cityIds=a&cityIds=b`).
// `fastify-type-provider-zod` não faz coerção (ao contrário do ajv), então um
// `z.array(...)` cru rejeita o caso de valor único com 400. Este preprocess
// normaliza scalar→array antes da validação (vale p/ usuário com escopo de 1 cidade).
const queryUuidArray = z.preprocess((v) => (v === undefined || v === null ? undefined : Array.isArray(v) ? v : [v]), z.array(z.string().uuid()).optional());
export const CommonReportQuerySchema = z.object({
    range: ReportRangeEnum.optional().default('last30d'),
    /**
     * Início do período customizado. Aceita `YYYY-MM-DD` (dia de calendário no
     * fuso da organização) ou ISO 8601 completo com offset.
     *
     * O formato de dia existe porque o seletor da UI trabalha em dias, e um
     * `YYYY-MM-DD` cru interpretado como meia-noite UTC jogaria o recorte 4 horas
     * para trás na operação (UTC−4).
     */
    dateFrom: z
        .union([z.string().datetime({ offset: true }), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)])
        .optional(),
    /** Fim do período customizado. Mesmos formatos de `dateFrom` (dia = dia inteiro). */
    dateTo: z
        .union([z.string().datetime({ offset: true }), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)])
        .optional(),
    cityIds: queryUuidArray,
    /**
     * Filtro "por pessoa" — `users.id` de quem se quer analisar.
     *
     * Não é um filtro de coluna: o backend resolve o ESCOPO EFETIVO dessa pessoa
     * e o aplica ao relatório inteiro (ver ReportPerson.scopeKind):
     *   - papel global (admin/gestor_geral) → sem restrição;
     *   - papel por cidade (gestor_regional) → as cidades dela;
     *   - papel operacional (agente)        → o que está atribuído a ela.
     *
     * Único por consulta: unir escopos de várias pessoas produz números que não
     * somam (uma cidade pertence a vários gestores) e não tem leitura de negócio.
     */
    personId: z.string().uuid().optional(),
    channel: z.string().optional(),
    status: z.string().optional(),
    origin: z.string().optional(),
    compareWithPrevious: z.boolean().optional().default(false),
});
// ---------------------------------------------------------------------------
// Pessoas selecionáveis no filtro (GET /api/reports/people)
//
// LGPD: nome de colaborador + papel. Não é PII de cidadão — mesma classe de
// dado que o ranking nominal de produtividade já expõe para gestores.
// ---------------------------------------------------------------------------
export const ReportPersonScopeKindEnum = z.enum(['global', 'city', 'self']);
export const ReportPersonSchema = z.object({
    /** users.id — é o valor que vai em `personId`. */
    userId: z.string().uuid(),
    displayName: z.string(),
    roleLabel: z.string(),
    /** Como o escopo dessa pessoa será interpretado ao filtrar. */
    scopeKind: ReportPersonScopeKindEnum,
    /** Quantas cidades a pessoa cobre. null quando scopeKind='global'. */
    cityCount: z.number().int().nonnegative().nullable(),
});
export const ReportPeopleResponseSchema = z.object({
    people: z.array(ReportPersonSchema),
});
// ---------------------------------------------------------------------------
// Shape auxiliar
// ---------------------------------------------------------------------------
const ReportRangeInfoSchema = z.object({
    from: z.string(),
    to: z.string(),
    label: z.string(),
    scope: ReportScopeEnum,
    /** Fuso IANA que define os dias do recorte (ex: 'America/Porto_Velho'). */
    timeZone: z.string(),
    /** Primeiro dia de calendário local do intervalo (YYYY-MM-DD). */
    fromDay: z.string(),
    /** Último dia de calendário local do intervalo (YYYY-MM-DD). */
    toDay: z.string(),
});
// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
export const OverviewQuerySchema = CommonReportQuerySchema;
const OverviewLeadsSchema = z.object({
    /**
     * Leads criados DENTRO do período.
     *
     * Era all-time enquanto a tela imprimia o rótulo do período em cima dele —
     * "TOTAL DE LEADS 4.080 — Hoje". O tamanho da base virou `totalAllTime`.
     */
    total: z.number().int().nonnegative(),
    /** Tamanho da base no escopo, sem janela temporal (snapshot). */
    totalAllTime: z.number().int().nonnegative(),
    newInPeriod: z.number().int().nonnegative(),
    /** Concluídos no período (estágio atual ganho + última transição na janela). */
    closedWon: z.number().int().nonnegative(),
    /** `closedWon / newInPeriod * 100`; `null` quando não há leads novos (≠ 0%). */
    conversionRate: z.number().nonnegative().nullable(),
});
const OverviewSimulationsSchema = z.object({
    total: z.number().int().nonnegative(),
    amountSum: z.number().nonnegative(),
    amountAvg: z.number().nonnegative(),
});
const OverviewContractsSchema = z.object({
    active: z.number().int().nonnegative(),
    settled: z.number().int().nonnegative(),
    defaulted: z.number().int().nonnegative(),
    activePrincipalSum: z.number().nonnegative(),
});
const OverviewConversationsSchema = z.object({
    open: z.number().int().nonnegative(),
    resolved: z.number().int().nonnegative(),
});
const OverviewPreviousPeriodSchema = z.object({
    leads: z.object({
        total: z.number().int().nonnegative(),
        closedWon: z.number().int().nonnegative(),
        conversionRate: z.number().nonnegative().nullable(),
    }),
    simulations: z.object({
        total: z.number().int().nonnegative(),
        amountSum: z.number().nonnegative(),
    }),
});
/**
 * Ponto da série diária da Visão Geral (D3).
 *
 * O dado sempre existiu — `mv_reports_overview` é diária — e era descartado na
 * agregação do endpoint. É a visão que a operação pede para comparar com o
 * fechamento ("por dia, por dia da semana").
 */
const OverviewSeriesItemSchema = z.object({
    /** Dia de calendário no fuso da organização (YYYY-MM-DD). */
    day: z.string(),
    leads: z.number().int().nonnegative(),
    conversations: z.number().int().nonnegative(),
    simulations: z.number().int().nonnegative(),
    contracts: z.number().int().nonnegative(),
});
export const OverviewResponseSchema = z.object({
    range: ReportRangeInfoSchema,
    /**
     * Instante da apuração (ISO 8601) — alimenta o "Atualizado às HH:MM" da UI.
     * Distinto de `refreshedAt`, que é a idade do DADO (D4).
     */
    generatedAt: z.string(),
    /**
     * Instante do último refresh bem-sucedido das materialized views, quando
     * conhecido. `null` = heartbeat indisponível.
     *
     * Sem isso a tela não tinha como distinguir "sem movimento" de "worker parado":
     * MV com lag de até 5 min mais cache de 3 min no cliente, ambos invisíveis.
     */
    refreshedAt: z.string().nullable(),
    /** Série diária do período — dias sem movimento vêm zerados. */
    series: z.array(OverviewSeriesItemSchema),
    leads: OverviewLeadsSchema,
    simulations: OverviewSimulationsSchema,
    contracts: OverviewContractsSchema,
    conversations: OverviewConversationsSchema,
    previousPeriod: OverviewPreviousPeriodSchema.optional(),
});
// ---------------------------------------------------------------------------
// Funnel
// ---------------------------------------------------------------------------
export const FunnelQuerySchema = CommonReportQuerySchema;
const FunnelStageSchema = z.object({
    stageId: z.string().uuid(),
    stageName: z.string(),
    stageOrder: z.number().int().nonnegative(),
    cardCount: z.number().int().nonnegative(),
    staleCardCount: z.number().int().nonnegative(),
    conversionToNextRate: z.number().nonnegative().nullable(),
    avgDwellHours: z.number().nonnegative().nullable(),
    medianDwellHours: z.number().nonnegative().nullable(),
});
export const FunnelResponseSchema = z.object({
    range: ReportRangeInfoSchema,
    stages: z.array(FunnelStageSchema),
});
// ---------------------------------------------------------------------------
// Attendance
// ---------------------------------------------------------------------------
export const AttendanceQuerySchema = CommonReportQuerySchema;
const AttendanceByChannelSchema = z.object({
    channel: z.string(),
    conversationCount: z.number().int().nonnegative(),
    messageCount: z.number().int().nonnegative(),
});
const AttendanceTimingsSchema = z.object({
    firstResponseAvgSec: z.number().nonnegative().nullable(),
    firstResponseP90Sec: z.number().nonnegative().nullable(),
    resolutionAvgSec: z.number().nonnegative().nullable(),
    resolutionP90Sec: z.number().nonnegative().nullable(),
});
export const AttendanceResponseSchema = z.object({
    range: ReportRangeInfoSchema,
    totals: z.object({
        conversationsOpened: z.number().int().nonnegative(),
        conversationsResolved: z.number().int().nonnegative(),
        messagesTotal: z.number().int().nonnegative(),
    }),
    byChannel: z.array(AttendanceByChannelSchema),
    timings: AttendanceTimingsSchema,
});
// ---------------------------------------------------------------------------
// Credit -- 4-E (F23-S04)
//
// Fonte principal: mv_reports_credit (colunas por produto/cidade).
// Zero PII: product_id e UUID opaco; city_id e UUID opaco.
// ---------------------------------------------------------------------------
export const CreditQuerySchema = CommonReportQuerySchema.extend({
    productIds: z.array(z.string().uuid()).optional(),
});
// Funil de credito: simulacao -> analise -> aprovacao -> contrato
const CreditFunnelSchema = z.object({
    simulations: z.number().int().nonnegative(),
    analyses: z.number().int().nonnegative(),
    analysesApproved: z.number().int().nonnegative(),
    analysesRefused: z.number().int().nonnegative(),
    analysesInProgress: z.number().int().nonnegative(),
    contracts: z.number().int().nonnegative(),
    simToAnalysisRate: z.number().nonnegative(),
    approvalRate: z.number().nonnegative(),
    simToContractRate: z.number().nonnegative(),
});
const CreditAmountsSchema = z.object({
    simulationsAmountSum: z.number().nonnegative(),
    simulationsAmountAvg: z.number().nonnegative(),
    simulationsTermAvg: z.number().nonnegative(),
    analysesApprovedAmountAvg: z.number().nonnegative(),
    contractsPrincipalSum: z.number().nonnegative(),
});
const CreditContractsByStatusSchema = z.object({
    active: z.number().int().nonnegative(),
    settled: z.number().int().nonnegative(),
    defaulted: z.number().int().nonnegative(),
    defaultRate: z.number().nonnegative(),
});
// Breakdown por produto (sem PII -- so UUID do produto)
const CreditByProductSchema = z.object({
    productId: z.string().uuid().nullable(),
    simulations: z.number().int().nonnegative(),
    analyses: z.number().int().nonnegative(),
    analysesApproved: z.number().int().nonnegative(),
    contracts: z.number().int().nonnegative(),
    principalSum: z.number().nonnegative(),
});
export const CreditResponseSchema = z.object({
    range: ReportRangeInfoSchema,
    funnel: CreditFunnelSchema,
    amounts: CreditAmountsSchema,
    contractsByStatus: CreditContractsByStatusSchema,
    byProduct: z.array(CreditByProductSchema),
});
// ---------------------------------------------------------------------------
// Collection -- 4-F (F23-S04)
//
// Fonte principal: mv_reports_collection (parcelas por status/cidade).
// Gating: billing:read (inclui gestor_regional city-scoped via F23-S02).
// Zero PII: todos os campos sao agregados.
// ---------------------------------------------------------------------------
export const CollectionQuerySchema = CommonReportQuerySchema;
// Carteira de cobranca (5 cards de status)
const CollectionWalletSchema = z.object({
    pending: z.number().int().nonnegative(),
    pendingAmountSum: z.number().nonnegative(),
    overdue: z.number().int().nonnegative(),
    overdueAmountSum: z.number().nonnegative(),
    paid: z.number().int().nonnegative(),
    paidAmountSum: z.number().nonnegative(),
    renegotiated: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
});
const CollectionRatesSchema = z.object({
    // Adimplencia = paid / (paid + overdue + pending)
    adimplenciaRate: z.number().nonnegative(),
    // Inadimplencia = overdue / (paid + overdue + pending)
    inadimplenciaRate: z.number().nonnegative(),
    avgDaysOverdue: z.number().nonnegative(),
});
// Eficiencia dos jobs de cobranca
const CollectionJobsEfficiencySchema = z.object({
    scheduled: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    paidBeforeSend: z.number().int().nonnegative(),
    sendRate: z.number().nonnegative(),
    failRate: z.number().nonnegative(),
});
export const CollectionResponseSchema = z.object({
    range: ReportRangeInfoSchema,
    wallet: CollectionWalletSchema,
    rates: CollectionRatesSchema,
    jobsEfficiency: CollectionJobsEfficiencySchema,
});
// ---------------------------------------------------------------------------
// Productivity -- 4-G (F23-S04) -- D3
//
// Gating: dashboard:read_by_agent.
// D3: self-scoped (agente) ve so o proprio registro + media anonima da equipe.
//     Gestor recebe ranking nominal completo (agentId UUID + displayName).
//
// LGPD: displayName e dado de colaborador (nao PII de cidadao), OK para gestores.
//       Para self-scoped, displayName e null nos registros de colegas (nao expostos).
// ---------------------------------------------------------------------------
export const ProductivityQuerySchema = CommonReportQuerySchema;
// Uma linha do ranking de produtividade — por PESSOA, não por agente (F31-S02).
//
// A query antiga comecava em `FROM agents`, mas 3 das 5 metricas sao acoes de
// usuario (mensagem enviada, conversa resolvida, simulacao criada). Quem atende
// sem ter carteira de leads — gestor geral, por exemplo — simplesmente sumia do
// ranking. Em producao isso escondia a atendente mais ativa do sistema.
//
// A separacao agora e explicita:
//   personId + metricas de ATIVIDADE  -> users (quem fez o trabalho)
//   agentId  + metricas de CARTEIRA   -> agents (de quem e o lead)
const ProductivityPersonRowSchema = z.object({
    /** users.id — a pessoa que executou as acoes. Sempre presente. */
    personId: z.string().uuid(),
    /**
     * agents.id — presente apenas quando a pessoa tambem tem carteira de leads.
     * null e o caso normal de quem atende mas nao recebe lead atribuido.
     */
    agentId: z.string().uuid().nullable(),
    // displayName presente para gestores (ranking nominal); null para self-scoped colegas (D3)
    displayName: z.string().nullable(),
    // --- atividade (fonte: users) ---
    messagesSent: z.number().int().nonnegative(),
    conversationsResolved: z.number().int().nonnegative(),
    simulationsCreated: z.number().int().nonnegative(),
    // --- carteira (fonte: agents) ---
    leadsClosedWon: z.number().int().nonnegative(),
    contractsOriginated: z.number().int().nonnegative(),
    avgFirstResponseSec: z.number().nonnegative().nullable(),
});
// Media anonima da equipe (retornada apenas em self-scoped -- D3)
const ProductivityTeamAverageSchema = z.object({
    messagesSent: z.number().nonnegative(),
    leadsClosedWon: z.number().nonnegative(),
    simulationsCreated: z.number().nonnegative(),
    conversationsResolved: z.number().nonnegative(),
    contractsOriginated: z.number().nonnegative(),
});
export const ProductivityResponseSchema = z.object({
    range: ReportRangeInfoSchema,
    // Para gestor: toda pessoa com papel operacional. Para self-scoped: so a propria.
    // Nome do campo e `people`, nao `agents`: a lista inclui quem nao tem carteira.
    people: z.array(ProductivityPersonRowSchema),
    // Presente apenas em self-scoped (D3): media agregada anonima da equipe.
    teamAverage: ProductivityTeamAverageSchema.optional(),
});
// ---------------------------------------------------------------------------
// AI / Pre-attendance -- §4-C (F23-S05)
//
// Fonte: ai_conversation_states, ai_decision_logs, chatwoot_handoffs.
// Gating: dashboard:read + flag ai.livechat_agent.enabled.
// Zero PII: sem phone, sem summary de handoff, sem lead_id em texto livre.
// Custo de LLM: tokens × tarifa por modelo. Se tarifa indisponivel no catalogo,
//   costAvailable=false e estimatedCostUsd=null (nao inventar tarifa).
// ---------------------------------------------------------------------------
export const AiQuerySchema = CommonReportQuerySchema;
// Saude de conversas IA (contagens e taxas)
const AiConversationHealthSchema = z.object({
    total: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    handoffed: z.number().int().nonnegative(),
    handoffRate: z.number().nonnegative(),
    completedWithoutHandoff: z.number().int().nonnegative(),
});
// Motivos de handoff (breakdown por reason)
const AiHandoffReasonRowSchema = z.object({
    reason: z.string(),
    count: z.number().int().nonnegative(),
    rate: z.number().nonnegative(),
});
// Distribuicao por no do grafo (intenção/decisão)
const AiNodeDistributionRowSchema = z.object({
    nodeName: z.string(),
    callCount: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    errorRate: z.number().nonnegative(),
    avgLatencyMs: z.number().nonnegative().nullable(),
});
// Metricas de LLM (tokens, custo, latencia)
const AiLlmMetricsSchema = z.object({
    totalTokensIn: z.number().int().nonnegative(),
    totalTokensOut: z.number().int().nonnegative(),
    totalCalls: z.number().int().nonnegative(),
    // custo estimado em USD. null se tarifa nao disponivel no catalogo.
    estimatedCostUsd: z.number().nonnegative().nullable(),
    // false = tarifa nao disponivel para um ou mais modelos usados
    costAvailable: z.boolean(),
    avgLatencyMs: z.number().nonnegative().nullable(),
    p90LatencyMs: z.number().nonnegative().nullable(),
    errorRate: z.number().nonnegative(),
});
// Breakdown por modelo (tokens + custo por model key)
const AiModelBreakdownRowSchema = z.object({
    model: z.string(),
    callCount: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    estimatedCostUsd: z.number().nonnegative().nullable(),
    costAvailable: z.boolean(),
});
// SLA de handoff (tempo entre criacao e accepted/resolved)
const AiHandoffSlaSchema = z.object({
    avgTimeToAcceptSec: z.number().nonnegative().nullable(),
    p90TimeToAcceptSec: z.number().nonnegative().nullable(),
    pendingHandoffs: z.number().int().nonnegative(),
});
export const AiResponseSchema = z.object({
    range: z.object({
        from: z.string(),
        to: z.string(),
        label: z.string(),
        scope: ReportScopeEnum,
    }),
    conversations: AiConversationHealthSchema,
    handoffReasons: z.array(AiHandoffReasonRowSchema),
    nodeDistribution: z.array(AiNodeDistributionRowSchema),
    llmMetrics: AiLlmMetricsSchema,
    modelBreakdown: z.array(AiModelBreakdownRowSchema),
    handoffSla: AiHandoffSlaSchema,
});
// ---------------------------------------------------------------------------
// Audit & Operations -- §4-H (F23-S05)
//
// Fonte: audit_logs, event_outbox, event_dlq.
// Gating: audit:read (admin/gestor_geral).
// Zero PII: sem before/after dos audit_logs, sem payload de evento.
//   Apenas contagens, taxas e IDs opacos de resource_type/action.
// ---------------------------------------------------------------------------
export const AuditQuerySchema = CommonReportQuerySchema;
// Resumo de acoes por tipo (ex: "leads.created" -> 42)
const AuditActionRowSchema = z.object({
    action: z.string(),
    count: z.number().int().nonnegative(),
});
// Resumo de acoes criticas (alteracoes de usuario, flags, permissoes)
const AuditCriticalActionRowSchema = z.object({
    action: z.string(),
    count: z.number().int().nonnegative(),
    actorCount: z.number().int().nonnegative(),
});
// Volume total de audit logs no periodo
const AuditVolumeSchema = z.object({
    total: z.number().int().nonnegative(),
    byResourceType: z.array(z.object({
        resourceType: z.string(),
        count: z.number().int().nonnegative(),
    })),
});
// Saude do outbox (eventos processados, pendentes, falhos)
const EventOutboxHealthSchema = z.object({
    totalCreated: z.number().int().nonnegative(),
    totalProcessed: z.number().int().nonnegative(),
    totalPending: z.number().int().nonnegative(),
    totalFailed: z.number().int().nonnegative(),
    successRate: z.number().nonnegative(),
    avgProcessingLatencySec: z.number().nonnegative().nullable(),
});
// DLQ snapshot (itens pendentes de reprocessamento)
const EventDlqSnapshotSchema = z.object({
    pendingReprocess: z.number().int().nonnegative(),
    totalMoved: z.number().int().nonnegative(),
    // breakdown por event_name (top N)
    topEventNames: z.array(z.object({
        eventName: z.string(),
        count: z.number().int().nonnegative(),
    })),
});
export const AuditResponseSchema = z.object({
    range: z.object({
        from: z.string(),
        to: z.string(),
        label: z.string(),
        scope: ReportScopeEnum,
    }),
    auditVolume: AuditVolumeSchema,
    topActions: z.array(AuditActionRowSchema),
    criticalActions: z.array(AuditCriticalActionRowSchema),
    outboxHealth: EventOutboxHealthSchema,
    dlqSnapshot: EventDlqSnapshotSchema,
});
// ---------------------------------------------------------------------------
// Export -- §6 (F23-S09)
//
// POST /api/reports/export
// Gating: reports:export + flag reports.export.enabled.
// LGPD: apenas agregados exportados -- nenhum CPF/telefone/nome de cidadão.
// Seções disponíveis espelham as rotas GET existentes.
// ---------------------------------------------------------------------------
export const ReportSectionEnum = z.enum([
    'overview',
    'funnel',
    'attendance',
    'credit',
    'collection',
    'productivity',
    'ai',
    'audit',
]);
export const ExportFormatEnum = z.enum(['csv', 'xlsx', 'pdf']);
// Filters reutiliza CommonReportQuerySchema + campos específicos de seção.
// O backend valida e rejeita filtros fora do escopo do papel.
export const ExportFiltersSchema = CommonReportQuerySchema.extend({
    productIds: z.array(z.string().uuid()).optional(),
});
export const ExportRequestSchema = z.object({
    section: ReportSectionEnum,
    format: ExportFormatEnum,
    filters: ExportFiltersSchema.optional().default({}),
});
// Response de erro orientado (limite de linhas excedido)
export const ExportLimitErrorSchema = z.object({
    error: z.literal('EXPORT_LIMIT_EXCEEDED'),
    message: z.string(),
    rowCount: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
});
//# sourceMappingURL=reports.js.map